// Administration navigation grouping (IA redesign — see docs/admin-nav-redesign.md).
//
// This is PRESENTATION ONLY: it maps the existing Administration item ids (built in
// TenantAwareNavigationSidebar with their exact permission/entitlement gates) into
// collapsible groups so the section scales instead of growing into one flat 24-item list.
// It defines NO permission logic and NO routes — an item that is hidden by its gate stays
// hidden, and a group with no visible items disappears (automatic progressive disclosure).
//
// i18n: each group label falls back to `label` here; the translation key is
// `navigation:admin_group_<id>`.
import type { LucideIcon } from 'lucide-react';
import { Building2, Users2, ClipboardList, ScrollText, Wallet, ShieldCheck } from 'lucide-react';

export interface AdminGroupDef {
  id: string;
  label: string;
  icon: LucideIcon;
  itemIds: string[]; // ids from the sidebar's adminItems array
}

export const ADMIN_GROUPS: AdminGroupDef[] = [
  {
    id: 'organization',
    label: 'Organization',
    icon: Building2,
    itemIds: ['admin-setup', 'admin-users', 'admin-roles', 'admin-scope-policy', 'admin-delegations', 'admin-delegation-policy', 'admin-departments', 'admin-job-titles', 'admin-settings'],
  },
  {
    id: 'people',
    label: 'People Operations',
    icon: Users2,
    itemIds: ['admin-import', 'admin-migration', 'admin-lifecycle', 'admin-lifecycle-packages', 'admin-document-requirements', 'admin-document-compliance'],
  },
  {
    id: 'approvals',
    label: 'Approvals',
    icon: ClipboardList,
    // Interim home for operational approvals; promoted to a top-level Approvals area in a
    // later phase (nav-only, existing routes). Kept here so nothing is lost meanwhile.
    itemIds: ['admin-change-approvals', 'admin-payment-approvals'],
  },
  {
    id: 'policies',
    label: 'Policies & Config',
    icon: ScrollText,
    itemIds: ['admin-leave-policies', 'admin-holidays', 'admin-learning', 'admin-work-access', 'admin-workload', 'admin-ai'],
  },
  {
    id: 'finance',
    label: 'Payroll, Benefits & Finance',
    icon: Wallet,
    itemIds: ['admin-benefits', 'admin-payments', 'admin-payouts', 'admin-billing'],
  },
  {
    id: 'platform',
    label: 'Security, Workforce & Platform',
    icon: ShieldCheck,
    itemIds: ['admin-reports', 'admin-compliance', 'admin-audit', 'admin-workforce', 'admin-developer', 'admin-automation'],
  },
];

// Any admin item not explicitly mapped above lands here — a safety net so a newly-added
// item is never silently dropped from the sidebar.
export const ADMIN_GROUP_FALLBACK = 'platform';

const MAPPED_IDS = new Set(ADMIN_GROUPS.flatMap((g) => g.itemIds));

/** True if the given admin item id is explicitly placed in a group. */
export function isAdminItemMapped(itemId: string): boolean {
  return MAPPED_IDS.has(itemId);
}
